import { useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import type {
  CreatePricingRequest,
  ModelPricingRecord,
  ModelProtocol,
  ModelProtocolRecord,
  ModelRecord,
} from '@nadoutong/contracts';
import { KNOWN_MODEL_PROTOCOLS, isModelProtocol } from '@nadoutong/contracts';
import { OpsApiError } from '../api/client.ts';
import {
  useAddProtocol,
  useCreatePricing,
  useDeleteModel,
  useDeleteProtocol,
  useModel,
  useModelPricing,
  useModelProtocols,
  useUpdateModel,
} from '../api/useModels.ts';
import { ChannelPricingCard } from '../components/ChannelPricingCard.tsx';
import { DeprecationCard } from '../components/DeprecationCard.tsx';
import { Modal } from '../components/Modal.tsx';
import { useToast, coerceErrorMessage } from '../components/Toast.tsx';
import { useT } from '../i18n/index.ts';
import type { I18nKey } from '../i18n/index.ts';
import { INPUT_CLASS } from '../styles/forms.ts';

/**
 * `/supply/models/:vendor/:model` detail view. Four sections:
 *   1. Basic info — display_name (inline edit; PATCH on blur or Enter)
 *   2. Pricing history — newest-first table with the current row pinned;
 *      "Add pricing row" form for append-only updates
 *   3. Protocols — chips for registered protocols + add / delete
 *   4. Danger zone — soft-delete the model
 */

const DECIMAL_PLACES = 6;

function formatRate(value: number): string {
  return `$${value.toFixed(DECIMAL_PLACES)} / Mtok`;
}

function vendorOf(value: string | undefined): string {
  return value ?? '';
}

export function ModelDetail(): JSX.Element {
  const t = useT();
  const params = useParams<{ vendor: string; model: string }>();
  const vendor = vendorOf(params.vendor);
  const model = vendorOf(params.model);
  const detail = useModel(vendor, model);

  if (detail.isPending) {
    return (
      <section className="flex flex-col gap-8" data-testid="model-detail-loading">
        <div className="h-8 w-64 animate-pulse rounded-lg bg-surface-container" aria-hidden />
        <div className="h-32 animate-pulse rounded-xl bg-surface-container" aria-hidden />
        <div className="h-48 animate-pulse rounded-xl bg-surface-container" aria-hidden />
        <span className="sr-only">{t('model_detail.loading_aria')}</span>
      </section>
    );
  }

  if (detail.isError) {
    return (
      <section
        className="flex flex-col gap-4 rounded-xl bg-error-container p-6 text-on-error-container"
        role="alert"
        data-testid="model-detail-error"
      >
        <h2 className="text-base font-semibold">{t('model_detail.error.title')}</h2>
        <p className="text-sm">
          {detail.error instanceof OpsApiError
            ? `${detail.error.code}: ${detail.error.message}`
            : t('common.network_error')}
        </p>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => void detail.refetch()}
            className="inline-flex items-center justify-center rounded-lg bg-surface-container-highest px-4 py-2 text-[11px] font-bold uppercase tracking-[0.08em] text-on-surface transition-colors hover:bg-surface-bright"
          >
            {t('common.retry')}
          </button>
          <Link to="/supply/models" className="text-[11px] font-bold uppercase tracking-[0.08em]">
            {t('model_detail.back')}
          </Link>
        </div>
      </section>
    );
  }

  return <ModelDetailReady record={detail.data} />;
}

interface ReadyProps {
  readonly record: ModelRecord;
}

function ModelDetailReady({ record }: ReadyProps): JSX.Element {
  const t = useT();
  const pricingQ = useModelPricing(record.vendor, record.model);
  const currentPricing = useMemo(() => {
    if (pricingQ.data === undefined || pricingQ.data.length === 0) return null;
    const now = new Date().toISOString();
    const effective = pricingQ.data.filter((p) => p.effective_from <= now);
    if (effective.length === 0) return null;
    return effective[0] ?? null;
  }, [pricingQ.data]);
  return (
    <section className="flex flex-col gap-8" data-testid="model-detail-page">
      <nav aria-label="breadcrumb" className="flex items-center gap-2 text-[11px] text-outline">
        <Link to="/supply/models" className="hover:text-primary">
          {t('model_detail.breadcrumb.models')}
        </Link>
        <span aria-hidden>/</span>
        <span className="font-mono text-on-surface-variant">{record.vendor}</span>
        <span aria-hidden>/</span>
        <span className="font-mono font-semibold text-on-surface">{record.model}</span>
      </nav>

      <header className="flex flex-col gap-3">
        <h2
          className="text-3xl font-semibold tracking-tight text-on-surface"
          data-testid="model-detail-title"
        >
          {record.display_name ?? record.model}
        </h2>
        <div className="flex flex-wrap items-center gap-3 text-[10px] font-bold uppercase tracking-[0.1em] text-on-surface-variant">
          <span className="font-mono normal-case tracking-normal text-on-surface-variant">
            {record.vendor}/{record.model}
          </span>
          {record.deleted_at !== null ? (
            <>
              <span aria-hidden>·</span>
              <span className="text-error">
                {t('model_detail.meta.soft_deleted', { date: record.deleted_at })}
              </span>
            </>
          ) : null}
          {record.deprecated_at !== null ? (
            <>
              <span aria-hidden>·</span>
              <span
                data-testid="model-header-deprecated-chip"
                className="inline-flex items-center rounded-full bg-tertiary-container px-3 py-1 text-[10px] font-bold uppercase tracking-[0.08em] text-on-tertiary-container"
              >
                {t('model_detail.meta.deprecated', { date: record.deprecated_at })}
              </span>
            </>
          ) : null}
        </div>
      </header>

      <BasicInfoCard record={record} />
      <PricingCard vendor={record.vendor} model={record.model} />
      <ChannelPricingCard vendor={record.vendor} model={record.model} currentPricing={currentPricing} />
      <ProtocolsCard vendor={record.vendor} model={record.model} />
      <DeprecationCard record={record} />
      <DangerZone record={record} />
    </section>
  );
}

interface BasicInfoProps {
  readonly record: ModelRecord;
}

function BasicInfoCard({ record }: BasicInfoProps): JSX.Element {
  const t = useT();
  const update = useUpdateModel();
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);

  async function handlePatch(body: Parameters<typeof update.mutateAsync>[0]['body']): Promise<void> {
    setError(null);
    try {
      await update.mutateAsync({ vendor: record.vendor, model: record.model, body });
    } catch (err) {
      const message =
        err instanceof OpsApiError
          ? `${err.code}: ${err.message}`
          : coerceErrorMessage(err);
      setError(message);
      toast.error(message);
    }
  }

  return (
    <article
      data-testid="model-basic-card"
      className="flex flex-col gap-6 rounded-xl bg-surface-container p-8"
    >
      <header className="flex items-center justify-between">
        <h3 className="text-[11px] font-bold uppercase tracking-[0.1em] text-on-surface-variant">
          {t('model_detail.basic.title')}
        </h3>
        {update.isPending ? (
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline">
            {t('common.saving')}
          </span>
        ) : null}
      </header>
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        <InlineTextField
          label={t('model_detail.basic.display_name')}
          initial={record.display_name ?? ''}
          testId="model-basic-display-name"
          onSubmit={(value) =>
            handlePatch({ display_name: value.length === 0 ? null : value })
          }
        />
      </div>
      {error !== null ? (
        <p
          className="text-xs font-medium text-error"
          role="alert"
          data-testid="model-basic-error"
        >
          {error}
        </p>
      ) : null}
    </article>
  );
}

interface InlineTextFieldProps {
  readonly label: string;
  readonly initial: string;
  readonly testId: string;
  readonly onSubmit: (value: string) => Promise<void> | void;
}

function InlineTextField({ label, initial, testId, onSubmit }: InlineTextFieldProps): JSX.Element {
  const [value, setValue] = useState(initial);
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    setValue(initial);
    setDirty(false);
  }, [initial]);

  async function commit(): Promise<void> {
    if (!dirty) return;
    const trimmed = value.trim();
    await onSubmit(trimmed);
    setDirty(false);
  }

  return (
    <label className="flex flex-col gap-2">
      <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
        {label}
      </span>
      <input
        type="text"
        value={value}
        onChange={(e) => {
          setValue(e.currentTarget.value);
          setDirty(true);
        }}
        onBlur={() => void commit()}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            void commit();
          }
        }}
        data-testid={testId}
        className="w-full rounded-lg bg-surface-container-lowest px-3 py-2 text-sm text-on-surface border-0 border-b border-outline-variant focus:border-primary focus:outline-none focus:ring-0 transition-colors"
      />
    </label>
  );
}

interface PricingProps {
  readonly vendor: string;
  readonly model: string;
}

// Today as YYYY-MM-DD (UTC). Matches `<input type="date">`'s value
// format *and* the submit path, which interprets a bare YYYY-MM-DD as
// midnight UTC. Keeping both sides UTC avoids silent DST / timezone
// drift when an operator in UTC+N picks "today" and the server reads
// the resulting ISO-8601 timestamp.
function todayYmdUtc(): string {
  return new Date().toISOString().slice(0, 10);
}

function PricingCard({ vendor, model }: PricingProps): JSX.Element {
  const t = useT();
  const pricing = useModelPricing(vendor, model);
  const create = useCreatePricing();
  const toast = useToast();
  const [effectiveFrom, setEffectiveFrom] = useState<string>(() => todayYmdUtc());
  const [inputRate, setInputRate] = useState('');
  const [outputRate, setOutputRate] = useState('');
  const [cacheRead, setCacheRead] = useState('');
  const [cacheWrite, setCacheWrite] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Pricing list comes back newest-first per contract — the first row is
  // the currently-effective generation (assuming no future-dated rows).
  const sorted = useMemo<ModelPricingRecord[]>(() => pricing.data ?? [], [pricing.data]);
  const currentEffective = useMemo<string | null>(() => {
    const now = Date.now();
    for (const row of sorted) {
      const ts = Date.parse(row.effective_from);
      if (Number.isFinite(ts) && ts <= now) {
        return row.effective_from;
      }
    }
    return null;
  }, [sorted]);

  function parseRate(raw: string, invalidKey: I18nKey): number | null {
    const trimmed = raw.trim();
    if (trimmed.length === 0) return null;
    const next = Number(trimmed);
    if (!Number.isFinite(next) || next < 0) {
      throw new Error(t(invalidKey));
    }
    return next;
  }

  async function handleCreate(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    if (effectiveFrom.trim().length === 0) {
      setError(t('model_detail.pricing.error.effective_from_required'));
      return;
    }
    let isoTimestamp: string;
    try {
      // Date-input value (YYYY-MM-DD) → midnight UTC ISO-8601.
      const raw = effectiveFrom.trim();
      const parsed =
        raw.includes('T') ? new Date(raw) : new Date(`${raw}T00:00:00Z`);
      const ts = parsed.getTime();
      if (!Number.isFinite(ts)) {
        setError(t('model_detail.pricing.error.effective_from_invalid'));
        return;
      }
      isoTimestamp = parsed.toISOString();
    } catch {
      setError(t('model_detail.pricing.error.effective_from_invalid'));
      return;
    }
    let inputUsd: number;
    let outputUsd: number;
    let cacheReadUsd: number | null;
    let cacheWriteUsd: number | null;
    try {
      const inp = parseRate(inputRate, 'model_detail.pricing.error.input_rate_invalid');
      const out = parseRate(outputRate, 'model_detail.pricing.error.output_rate_invalid');
      if (inp === null || out === null) {
        setError(t('model_detail.pricing.error.rates_required'));
        return;
      }
      inputUsd = inp;
      outputUsd = out;
      cacheReadUsd = parseRate(cacheRead, 'model_detail.pricing.error.cache_read_invalid');
      cacheWriteUsd = parseRate(cacheWrite, 'model_detail.pricing.error.cache_write_invalid');
    } catch (err) {
      setError(err instanceof Error ? err.message : t('model_detail.pricing.error.invalid'));
      return;
    }
    const body: CreatePricingRequest = {
      effective_from: isoTimestamp,
      input_per_mtok_usd: inputUsd,
      output_per_mtok_usd: outputUsd,
      ...(cacheReadUsd !== null ? { cache_read_per_mtok_usd: cacheReadUsd } : {}),
      ...(cacheWriteUsd !== null ? { cache_write_per_mtok_usd: cacheWriteUsd } : {}),
    };
    try {
      await create.mutateAsync({ vendor, model, body });
      setEffectiveFrom(todayYmdUtc());
      setInputRate('');
      setOutputRate('');
      setCacheRead('');
      setCacheWrite('');
    } catch (err) {
      const message =
        err instanceof OpsApiError
          ? `${err.code}: ${err.message}`
          : coerceErrorMessage(err);
      setError(message);
      toast.error(message);
    }
  }

  return (
    <article
      data-testid="model-pricing-card"
      className="flex flex-col gap-6 rounded-xl bg-surface-container p-8"
    >
      <header className="flex items-center justify-between">
        <h3 className="text-[11px] font-bold uppercase tracking-[0.1em] text-on-surface-variant">
          {t('model_detail.pricing.title')}
        </h3>
        {pricing.isPending ? (
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline">
            {t('model_detail.pricing.loading_aria')}
          </span>
        ) : null}
      </header>
      {pricing.isError ? (
        <p
          className="text-xs font-medium text-error"
          role="alert"
          data-testid="model-pricing-load-error"
        >
          {pricing.error instanceof OpsApiError
            ? `${pricing.error.code}: ${pricing.error.message}`
            : t('model_detail.pricing.error.load')}
        </p>
      ) : null}
      {pricing.isSuccess && sorted.length === 0 ? (
        <p className="text-sm text-outline" data-testid="model-pricing-empty">
          {t('model_detail.pricing.empty')}
        </p>
      ) : null}
      {sorted.length > 0 ? (
        <table
          className="w-full border-separate border-spacing-0"
          data-testid="model-pricing-table"
        >
          <thead>
            <tr className="bg-surface-container-high">
              <th
                scope="col"
                className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-[0.1em] text-on-surface-variant"
              >
                {t('model_detail.pricing.column.effective_from')}
              </th>
              <th
                scope="col"
                className="px-4 py-3 text-right text-[10px] font-bold uppercase tracking-[0.1em] text-on-surface-variant"
              >
                {t('model_detail.pricing.column.input')}
              </th>
              <th
                scope="col"
                className="px-4 py-3 text-right text-[10px] font-bold uppercase tracking-[0.1em] text-on-surface-variant"
              >
                {t('model_detail.pricing.column.output')}
              </th>
              <th
                scope="col"
                className="px-4 py-3 text-right text-[10px] font-bold uppercase tracking-[0.1em] text-on-surface-variant"
              >
                {t('model_detail.pricing.column.cache_read')}
              </th>
              <th
                scope="col"
                className="px-4 py-3 text-right text-[10px] font-bold uppercase tracking-[0.1em] text-on-surface-variant"
              >
                {t('model_detail.pricing.column.cache_write')}
              </th>
              <th
                scope="col"
                className="px-4 py-3 text-right text-[10px] font-bold uppercase tracking-[0.1em] text-on-surface-variant"
              >
                {t('model_detail.pricing.column.status')}
              </th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((row, i) => {
              const isCurrent = row.effective_from === currentEffective;
              const zebraClass =
                i % 2 === 0 ? 'bg-surface-container' : 'bg-surface-container-low';
              const highlightClass = isCurrent ? 'ring-1 ring-primary/40' : '';
              return (
                <tr
                  key={row.effective_from}
                  data-testid={`model-pricing-row-${row.effective_from}`}
                  className={`${zebraClass} ${highlightClass}`}
                >
                  <td className="px-4 py-3 font-mono text-[12px] text-on-surface-variant">
                    {row.effective_from}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-[12px] text-on-surface">
                    {formatRate(row.input_per_mtok_usd)}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-[12px] text-on-surface">
                    {formatRate(row.output_per_mtok_usd)}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-[12px] text-on-surface-variant">
                    {formatRate(row.cache_read_per_mtok_usd)}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-[12px] text-on-surface-variant">
                    {formatRate(row.cache_write_per_mtok_usd)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {isCurrent ? (
                      <span
                        data-testid="model-pricing-current-badge"
                        className="inline-flex items-center rounded-full bg-surface-container-highest px-3 py-1 text-[10px] font-bold uppercase tracking-[0.08em] text-primary"
                      >
                        {t('model_detail.pricing.row.current')}
                      </span>
                    ) : (
                      <span className="text-[10px] uppercase tracking-[0.08em] text-outline">
                        {t('model_detail.pricing.row.archived')}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : null}
      <form className="flex flex-col gap-4" onSubmit={handleCreate} noValidate>
        <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
          {t('model_detail.pricing.add.title')}
        </span>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-5">
          <label className="flex flex-col gap-2 sm:col-span-2">
            <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
              {t('model_detail.pricing.add.effective_from.label')}
            </span>
            <input
              type="date"
              value={effectiveFrom}
              onChange={(e) => setEffectiveFrom(e.currentTarget.value)}
              data-testid="model-pricing-effective-from"
              disabled={create.isPending}
              className={INPUT_CLASS}
            />
          </label>
          <label className="flex flex-col gap-2">
            <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
              {t('model_detail.pricing.add.input_rate.label')}
            </span>
            <input
              type="number"
              min="0"
              step="0.000001"
              value={inputRate}
              onChange={(e) => setInputRate(e.currentTarget.value)}
              data-testid="model-pricing-input-rate"
              disabled={create.isPending}
              placeholder={t('model_detail.pricing.add.input_rate.placeholder')}
              className={INPUT_CLASS}
            />
          </label>
          <label className="flex flex-col gap-2">
            <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
              {t('model_detail.pricing.add.output_rate.label')}
            </span>
            <input
              type="number"
              min="0"
              step="0.000001"
              value={outputRate}
              onChange={(e) => setOutputRate(e.currentTarget.value)}
              data-testid="model-pricing-output-rate"
              disabled={create.isPending}
              placeholder={t('model_detail.pricing.add.output_rate.placeholder')}
              className={INPUT_CLASS}
            />
          </label>
          <label className="flex flex-col gap-2">
            <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
              {t('model_detail.pricing.add.cache_read.label')}
            </span>
            <input
              type="number"
              min="0"
              step="0.000001"
              value={cacheRead}
              onChange={(e) => setCacheRead(e.currentTarget.value)}
              data-testid="model-pricing-cache-read"
              disabled={create.isPending}
              placeholder={t('model_detail.pricing.add.cache_read.placeholder')}
              className={INPUT_CLASS}
            />
          </label>
          <label className="flex flex-col gap-2">
            <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
              {t('model_detail.pricing.add.cache_write.label')}
            </span>
            <input
              type="number"
              min="0"
              step="0.000001"
              value={cacheWrite}
              onChange={(e) => setCacheWrite(e.currentTarget.value)}
              data-testid="model-pricing-cache-write"
              disabled={create.isPending}
              placeholder={t('model_detail.pricing.add.cache_write.placeholder')}
              className={INPUT_CLASS}
            />
          </label>
          <div className="sm:col-span-5 flex justify-end">
            <button
              type="submit"
              disabled={create.isPending}
              data-testid="model-pricing-submit"
              className="inline-flex items-center justify-center rounded-lg bg-primary-container px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-primary transition-all hover:shadow-[0_0_16px_rgba(173,198,255,0.2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-container disabled:opacity-40 disabled:pointer-events-none"
            >
              {create.isPending
                ? t('model_detail.pricing.add.submitting')
                : t('model_detail.pricing.add.submit')}
            </button>
          </div>
        </div>
      </form>
      {error !== null ? (
        <p
          className="text-xs font-medium text-error"
          role="alert"
          data-testid="model-pricing-error"
        >
          {error}
        </p>
      ) : null}
    </article>
  );
}

interface ProtocolsCardProps {
  readonly vendor: string;
  readonly model: string;
}

function ProtocolsCard({ vendor, model }: ProtocolsCardProps): JSX.Element {
  const t = useT();
  const protocols = useModelProtocols(vendor, model);
  const add = useAddProtocol();
  const remove = useDeleteProtocol();
  const toast = useToast();
  const [selected, setSelected] = useState<ModelProtocol>(KNOWN_MODEL_PROTOCOLS[0] ?? 'anthropic');
  const [error, setError] = useState<string | null>(null);

  const registered = useMemo<readonly ModelProtocolRecord[]>(
    () => protocols.data ?? [],
    [protocols.data],
  );
  const registeredSet = useMemo(
    () => new Set(registered.map((r) => r.protocol)),
    [registered],
  );

  async function handleAdd(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    if (registeredSet.has(selected)) {
      setError(t('model_detail.protocols.error.already_registered', { protocol: selected }));
      return;
    }
    try {
      await add.mutateAsync({ vendor, model, body: { protocol: selected } });
    } catch (err) {
      const message =
        err instanceof OpsApiError
          ? `${err.code}: ${err.message}`
          : coerceErrorMessage(err);
      setError(message);
      toast.error(message);
    }
  }

  async function handleDelete(protocol: ModelProtocol): Promise<void> {
    setError(null);
    try {
      await remove.mutateAsync({ vendor, model, protocol });
    } catch (err) {
      const message =
        err instanceof OpsApiError
          ? `${err.code}: ${err.message}`
          : coerceErrorMessage(err);
      setError(message);
      toast.error(message);
    }
  }

  return (
    <article
      data-testid="model-protocols-card"
      className="flex flex-col gap-6 rounded-xl bg-surface-container p-8"
    >
      <header className="flex items-center justify-between">
        <h3 className="text-[11px] font-bold uppercase tracking-[0.1em] text-on-surface-variant">
          {t('model_detail.protocols.title')}
        </h3>
        {protocols.isPending ? (
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline">
            {t('model_detail.protocols.loading_aria')}
          </span>
        ) : null}
      </header>
      {protocols.isError ? (
        <p
          className="text-xs font-medium text-error"
          role="alert"
          data-testid="model-protocols-load-error"
        >
          {protocols.error instanceof OpsApiError
            ? `${protocols.error.code}: ${protocols.error.message}`
            : t('model_detail.protocols.error.load')}
        </p>
      ) : null}
      <div className="flex flex-wrap gap-2" data-testid="model-protocols-chips">
        {registered.length === 0 && !protocols.isPending ? (
          <span className="text-sm text-outline" data-testid="model-protocols-empty">
            {t('model_detail.protocols.empty')}
          </span>
        ) : null}
        {registered.map((entry) => (
          <span
            key={entry.protocol}
            data-testid={`model-protocols-chip-${entry.protocol}`}
            className="inline-flex items-center gap-2 rounded-full bg-surface-container-highest px-3 py-1 text-[11px] font-bold uppercase tracking-[0.08em] text-primary"
          >
            <span>{entry.protocol}</span>
            <button
              type="button"
              onClick={() => void handleDelete(entry.protocol)}
              disabled={remove.isPending}
              aria-label={t('model_detail.protocols.remove_aria', { protocol: entry.protocol })}
              data-testid={`model-protocols-chip-${entry.protocol}-remove`}
              className="inline-flex h-4 w-4 items-center justify-center rounded-full text-on-surface-variant transition-colors hover:text-error focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-40"
            >
              <span aria-hidden="true" className="material-symbols-outlined text-[14px]">
                close
              </span>
            </button>
          </span>
        ))}
      </div>
      <form className="flex items-end gap-3" onSubmit={handleAdd} noValidate>
        <label className="flex flex-1 flex-col gap-2">
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
            {t('model_detail.protocols.add.label')}
          </span>
          <select
            value={selected}
            onChange={(e) => {
              const next = e.currentTarget.value;
              if (isModelProtocol(next)) {
                setSelected(next);
              }
            }}
            data-testid="model-protocols-select"
            disabled={add.isPending}
            className={INPUT_CLASS}
          >
            {KNOWN_MODEL_PROTOCOLS.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </label>
        <button
          type="submit"
          disabled={add.isPending}
          data-testid="model-protocols-add"
          className="inline-flex items-center justify-center rounded-lg bg-primary-container px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-primary transition-all hover:shadow-[0_0_16px_rgba(173,198,255,0.2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-container disabled:opacity-40 disabled:pointer-events-none"
        >
          {add.isPending
            ? t('model_detail.protocols.add.adding')
            : t('model_detail.protocols.add.submit')}
        </button>
      </form>
      {error !== null ? (
        <p
          className="text-xs font-medium text-error"
          role="alert"
          data-testid="model-protocols-error"
        >
          {error}
        </p>
      ) : null}
    </article>
  );
}

interface DangerZoneProps {
  readonly record: ModelRecord;
}

function DangerZone({ record }: DangerZoneProps): JSX.Element {
  const t = useT();
  const remove = useDeleteModel();
  const toast = useToast();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleConfirm(): Promise<void> {
    setError(null);
    try {
      await remove.mutateAsync({ vendor: record.vendor, model: record.model });
      setConfirmOpen(false);
    } catch (err) {
      const message =
        err instanceof OpsApiError
          ? `${err.code}: ${err.message}`
          : coerceErrorMessage(err);
      setError(message);
      toast.error(message);
    }
  }

  return (
    <article
      data-testid="model-danger-zone"
      className="flex flex-col gap-4 rounded-xl bg-surface-container p-8"
    >
      <header className="flex items-center gap-3">
        <h3 className="text-[11px] font-bold uppercase tracking-[0.1em] text-error">
          {t('model_detail.danger.title')}
        </h3>
      </header>
      <p className="text-sm text-outline">{t('model_detail.danger.body')}</p>
      <div>
        <button
          type="button"
          onClick={() => setConfirmOpen(true)}
          data-testid="model-delete"
          disabled={record.deleted_at !== null}
          className="inline-flex items-center justify-center rounded-lg bg-error-container px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-error-container transition-all hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-container disabled:opacity-40 disabled:pointer-events-none"
        >
          {record.deleted_at !== null
            ? t('model_detail.danger.already_deleted')
            : t('model_detail.danger.delete')}
        </button>
      </div>
      {confirmOpen ? (
        <Modal
          isOpen
          onClose={() => {
            if (!remove.isPending) {
              setConfirmOpen(false);
              setError(null);
            }
          }}
          titleId="model-delete-confirm-title"
          testId="model-delete-confirm"
        >
          <div className="flex flex-col gap-6 p-8">
            <h2
              id="model-delete-confirm-title"
              className="text-base font-semibold text-on-surface"
            >
              {t('model_detail.delete_modal.title', {
                vendor: record.vendor,
                model: record.model,
              })}
            </h2>
            <p className="text-sm text-outline">{t('model_detail.delete_modal.body')}</p>
            {error !== null ? (
              <p
                className="text-xs text-error"
                role="alert"
                data-testid="model-detail-delete-error"
              >
                {error}
              </p>
            ) : null}
            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  if (!remove.isPending) {
                    setConfirmOpen(false);
                    setError(null);
                  }
                }}
                disabled={remove.isPending}
                className="inline-flex items-center justify-center rounded-lg bg-surface-container px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-surface transition-colors hover:bg-surface-bright disabled:opacity-40 disabled:pointer-events-none"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={() => void handleConfirm()}
                disabled={remove.isPending}
                data-testid="model-detail-delete-confirm"
                className="inline-flex items-center justify-center rounded-lg bg-error-container px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-error-container transition-all hover:brightness-110 disabled:opacity-40 disabled:pointer-events-none"
              >
                {remove.isPending ? t('common.deleting') : t('common.delete')}
              </button>
            </div>
          </div>
        </Modal>
      ) : null}
    </article>
  );
}
